import { useState, useRef, useEffect } from 'react';
import { Ministry } from '../../../mocks/ministries';
import MinistryModal from './MinistryModal';
import VideoModal from './VideoModal';

interface MinistryCardProps {
  ministry: Ministry;
}

export default function MinistryCard({ ministry, onClick }: MinistryCardProps) {
  const [showModal, setShowModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [iframeSrc, setIframeSrc] = useState('');
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (isHovering && ministry.videoId) {
      // Set iframe src with autoplay when hovering
      setIframeSrc(`https://www.youtube.com/embed/${ministry.videoId}?autoplay=1&mute=1&controls=0&modestbranding=1&rel=0&showinfo=0`);
    } else {
      // Clear iframe src when not hovering to stop playback
      setIframeSrc('');
    }
  }, [isHovering, ministry.videoId]);

  const handleCardClick = () => {
    if (ministry.videoId) {
      setShowVideoModal(true);
    } else {
      setShowModal(true);
    }
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
    // Start preview play on hover
    if (iframeRef.current && ministry.videoId) {
      const iframe = iframeRef.current;
      iframe.src = `https://www.youtube.com/embed/${ministry.videoId}?autoplay=1&mute=1&controls=0&rel=0&modestbranding=1&loop=1&playlist=${ministry.videoId}`;
    }
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    // Stop preview on mouse leave
    if (iframeRef.current && ministry.videoId) {
      const iframe = iframeRef.current;
      iframe.src = `https://www.youtube.com/embed/${ministry.videoId}?controls=0&rel=0&modestbranding=1`;
    }
  };

  return (
    <>
      <div 
        className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-2xl transition-all duration-300 group cursor-pointer"
        onClick={handleCardClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="relative h-56 overflow-hidden">
          {ministry.videoId ? (
            <div className="relative w-full h-full">
              <iframe
                ref={iframeRef}
                src={`https://www.youtube.com/embed/${ministry.videoId}?controls=0&rel=0&modestbranding=1`}
                title={ministry.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                style={{ border: 'none' }}
              />
              
              {/* Overlay with play button - only show when not hovering */}
              {!isHovering && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center transition-opacity duration-300">
                  <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                    <i className="ri-play-fill text-emerald-600 text-3xl ml-1"></i>
                  </div>
                </div>
              )}

              {/* Duration badge */}
              {ministry.duration && (
                <div className="absolute bottom-3 right-3 px-2 py-1 bg-black/80 text-white text-xs font-semibold rounded">
                  {ministry.duration} min
                </div>
              )}

              {/* Language badge */}
              {ministry.language && (
                <div className="absolute top-3 right-3 px-2 py-1 bg-emerald-600 text-white text-xs font-semibold rounded">
                  {ministry.language}
                </div>
              )}
            </div>
          ) : (
            <>
              <img
                src={ministry.image}
                alt={ministry.title}
                className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
            </>
          )}
        </div>

        <div className="p-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className={`${ministry.icon} text-emerald-600 text-xl`}></i>
            </div>
            <h3 className="text-lg font-bold text-gray-900 flex-1" style={{ fontFamily: 'Noto Sans, system-ui, sans-serif' }}>
              {ministry.title}
            </h3>
          </div>

          <p className="text-sm text-gray-600 mb-4 line-clamp-2">
            {ministry.description}
          </p>

          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <span className="flex items-center">
                <i className="ri-group-line mr-1"></i>
                {ministry.members}
              </span>
              <span className="flex items-center">
                <i className="ri-calendar-line mr-1"></i>
                {ministry.meetingDay}
              </span>
            </div>

            <button
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors whitespace-nowrap"
            >
              {ministry.videoId ? (
                <>
                  <i className="ri-play-line mr-1"></i>
                  Watch Now
                </>
              ) : (
                <>
                  Learn More
                  <i className="ri-arrow-right-line ml-1"></i>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {showModal && !ministry.videoId && (
        <MinistryModal ministry={ministry} onClose={() => setShowModal(false)} />
      )}

      {showVideoModal && ministry.videoId && (
        <VideoModal 
          videoId={ministry.videoId} 
          title={ministry.title}
          onClose={() => setShowVideoModal(false)} 
        />
      )}
    </>
  );
}
